import { ref } from 'vue'
import { MenuService } from '@/services/menuService'
import { useAuthStore } from '@/stores/auth'
import { getEntityRights } from '@/services/entityRightsService'
import { checkPermission } from '@/modules/Inventory/utils/accessControl'

// Menu feed type that carries the Circulars / SOP Documents entities.
// (Authorize/Menu/GetMenuSubMenuList?Type=6)
const AUTH_MENU_TYPE = 6
// moduleId only selects the Authorize microservice base URL in MenuService.
const AUTHORIZE_MODULE_ID = 1

/**
 * Decides whether the current role may CREATE a given entity, reusing the app's
 * existing authorization stack instead of duplicating permission logic:
 *
 *   1. `Authorize/Menu/GetMenuSubMenuList?Type=6` → resolve the menu's EntityId
 *      (matched by name — this feed only exposes the Read-accessible menus, so it
 *      is the same source MOPSidebar uses to render menus).
 *   2. `getEntityRights` → role-scoped rights from
 *      `Authorize/EntityOperationsDeptRoleMap/CheckMenuRights`.
 *   3. `checkPermission(Rights, 'Add')` → the shared gate (same as MOPHeader's
 *      "Add New").
 *
 * @param target either an explicit EntityId (when known), or a predicate over the
 *               lower-cased SubMenuName/MenuName used to locate the target menu row
 *               in the Type=6 feed (e.g. name => name.includes('circular')).
 */
export function useCreatePermission(target: number | ((name: string) => boolean)) {
  const canCreate = ref(false)
  const authStore = useAuthStore()

  async function resolveEntityId(): Promise<number | undefined> {
    // Explicit EntityId — no menu lookup needed.
    if (typeof target === 'number') return target

    // Otherwise resolve it by name from the Type=6 feed.
    const roleId = authStore.user?.roleId
    const orgId = authStore.user?.organizationId
    if (!roleId || !orgId) return undefined
    const menus = await MenuService.getMenuSubMenuList(AUTH_MENU_TYPE, roleId, orgId, AUTHORIZE_MODULE_ID)
    return menus.find((m) => target((m.SubMenuName || m.MenuName || '').toLowerCase()))?.EntityId
  }

  async function resolve(): Promise<void> {
    try {
      const entityId = await resolveEntityId()
      if (!entityId) return

      // Role-scoped rights → shared "Add" permission gate.
      const rights = await getEntityRights(entityId)
      canCreate.value = checkPermission(rights, 'Add').hasPermission
    } catch (e) {
      console.error('useCreatePermission: failed to resolve create permission', e)
      canCreate.value = false
    }
  }

  return { canCreate, resolve }
}
